/**
 * Touch (tap counter) statistics for a discipline.
 *
 * Each judge's raw tap count for a round is stored on submission as
 * `touchesBlue` / `touchesRed` (see judgeSubmissions.{judge}.rounds[] in services.js) —
 * distinct from the translated TOD round score (`blue`/`red`). Multiple judges tap
 * independently on the same round, so a round's touch count is the average across the
 * judges who recorded one for that round, not a sum (summing would just multiply by
 * judge count rather than reflect actual bout intensity).
 */

/**
 * @param {object[]} bouts - all bouts for a discipline (any phase/status)
 * @returns {{
 *   totalBouts: number,
 *   totalTouches: number,
 *   avgTouchesPerBout: number,
 *   avgTouchesPerRound: number,
 *   topFighters: { name: string, touches: number }[],
 *   topFightersByAvg: { name: string, avgTouchesPerRound: number }[],
 *   topFightersByRatio: { name: string, ratio: number, touchesFor: number, touchesAgainst: number }[],
 *   topFights: { boutId: string, fighterBlue: string, fighterRed: string, totalTouches: number, rounds: number, avgTouchesPerRound: number, poolName?: string, phase: string }[],
 * }}
 */
export function calculateDisciplineTouchStats(bouts) {
  const fighterTouches = {};
  const fighterTouchesAgainst = {};
  const fighterRounds = {};
  const fightStats = [];
  let totalTouches = 0;
  let totalRounds = 0;

  (bouts || []).forEach((bout) => {
    const subs = bout.judgeSubmissions || {};
    const judgeKeys = Object.keys(subs);
    if (judgeKeys.length === 0) return;

    // Group each judge's recorded touch counts by round index
    const roundsByIndex = {};
    judgeKeys.forEach((k) => {
      (subs[k].rounds || []).forEach((r) => {
        if (typeof r.touchesBlue !== "number" && typeof r.touchesRed !== "number") return;
        const idx = r.index ?? 0;
        if (!roundsByIndex[idx]) roundsByIndex[idx] = [];
        roundsByIndex[idx].push({ blue: r.touchesBlue || 0, red: r.touchesRed || 0 });
      });
    });

    const roundIndices = Object.keys(roundsByIndex);
    if (roundIndices.length === 0) return;

    let boutBlueTouches = 0;
    let boutRedTouches = 0;
    roundIndices.forEach((idx) => {
      const entries = roundsByIndex[idx];
      // Round each round's cross-judge average to a whole touch before accumulating —
      // touches are physical taps, so fighters' totals must stay whole numbers even
      // though the per-round average across judges isn't.
      boutBlueTouches += Math.round(entries.reduce((s, e) => s + e.blue, 0) / entries.length);
      boutRedTouches += Math.round(entries.reduce((s, e) => s + e.red, 0) / entries.length);
    });

    const boutTotalTouches = boutBlueTouches + boutRedTouches;
    totalTouches += boutTotalTouches;
    totalRounds += roundIndices.length;

    fighterTouches[bout.fighterBlue] = (fighterTouches[bout.fighterBlue] || 0) + boutBlueTouches;
    fighterTouches[bout.fighterRed] = (fighterTouches[bout.fighterRed] || 0) + boutRedTouches;
    fighterTouchesAgainst[bout.fighterBlue] = (fighterTouchesAgainst[bout.fighterBlue] || 0) + boutRedTouches;
    fighterTouchesAgainst[bout.fighterRed] = (fighterTouchesAgainst[bout.fighterRed] || 0) + boutBlueTouches;
    fighterRounds[bout.fighterBlue] = (fighterRounds[bout.fighterBlue] || 0) + roundIndices.length;
    fighterRounds[bout.fighterRed] = (fighterRounds[bout.fighterRed] || 0) + roundIndices.length;

    fightStats.push({
      boutId: bout.id,
      fighterBlue: bout.fighterBlue,
      fighterRed: bout.fighterRed,
      poolName: bout.poolName,
      phase: bout.phase,
      totalTouches: boutTotalTouches,
      rounds: roundIndices.length,
      avgTouchesPerRound: boutTotalTouches / roundIndices.length,
    });
  });

  const totalBouts = fightStats.length;

  const topFighters = Object.entries(fighterTouches)
    .map(([name, touches]) => ({ name, touches }))
    .sort((a, b) => b.touches - a.touches)
    .slice(0, 5);

  const topFightersByAvg = Object.entries(fighterTouches)
    .map(([name, touches]) => ({ name, avgTouchesPerRound: Math.round((touches / fighterRounds[name]) * 10) / 10 }))
    .sort((a, b) => b.avgTouchesPerRound - a.avgTouchesPerRound)
    .slice(0, 5);

  // Touches landed vs. touches received — skip fighters who haven't been touched yet
  // (division by zero would produce a meaningless "infinite" ratio).
  const topFightersByRatio = Object.entries(fighterTouches)
    .filter(([name]) => fighterTouchesAgainst[name] > 0)
    .map(([name, touchesFor]) => ({ name, touchesFor, touchesAgainst: fighterTouchesAgainst[name], ratio: Math.round((touchesFor / fighterTouchesAgainst[name]) * 100) / 100 }))
    .sort((a, b) => b.ratio - a.ratio)
    .slice(0, 5);

  const topFights = [...fightStats]
    .sort((a, b) => b.avgTouchesPerRound - a.avgTouchesPerRound)
    .slice(0, 5)
    .map((f) => ({
      ...f,
      avgTouchesPerRound: Math.round(f.avgTouchesPerRound * 10) / 10,
    }));

  return {
    totalBouts,
    totalTouches: Math.round(totalTouches),
    avgTouchesPerBout: totalBouts ? Math.round((totalTouches / totalBouts) * 10) / 10 : 0,
    avgTouchesPerRound: totalRounds ? Math.round((totalTouches / totalRounds) * 10) / 10 : 0,
    topFighters,
    topFightersByAvg,
    topFightersByRatio,
    topFights,
  };
}
